import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PenTool, Plus, Eye, RotateCw, Link2, Trash2, FileDown, X, Loader2 } from "lucide-react";
import { NewDocWizard } from "@/components/fbsign/NewDocWizard";

const STATUS = {
  draft: ["Draft", "border-[var(--line)] text-[var(--ink-2)] bg-white"],
  in_progress: ["In Progress", "border-[#0369A1] text-[#0369A1] bg-[#F0F9FF]"],
  completed: ["Completed", "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]"],
  deleted: ["Deleted", "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]"],
};
const fmt = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export async function downloadBlob(url, name) {
  const r = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = href; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

export default function FBSign() {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("");
  const [wizard, setWizard] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = () => api.get("/fbsign", { params: { include_deleted: filter === "deleted" } }).then((r) => setDocs(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const shown = docs.filter((d) => (filter ? d.status === filter : true));
  const counts = docs.reduce((a, d) => ({ ...a, [d.status]: (a[d.status] || 0) + 1 }), {});

  async function copyLink(d) {
    const links = d.recipients.filter((r) => r.token).map((r) => `${d.recipients.length > 1 ? r.name + ": " : ""}${window.location.origin}/fbsign/s/${r.token}`);
    if (!links.length) { toast.error("Send the document first to create links"); return; }
    try { await navigator.clipboard.writeText(links.join("\n")); } catch (e) {}
    toast.success("Signing link copied");
  }
  async function resend(d) {
    try { const r = await api.post(`/fbsign/${d.id}/resend`); toast.success(`Reminder sent to ${r.data.emailed} recipient(s)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
  }
  async function remove(d) {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    await api.delete(`/fbsign/${d.id}`);
    toast.success("Deleted");
    load();
  }

  return (
    <div className="px-5 py-4 max-w-7xl" data-testid="fbsign-page">
      <div className="flex items-center gap-3 mb-3">
        <h1 className="text-lg font-serif-strong leading-none flex items-center gap-2"><PenTool size={16} /> FBSign</h1>
        <div className="h-4 w-px bg-[var(--line)]" />
        <div className="flex items-center gap-1" data-testid="fbsign-filters">
          {[["", "All"], ["draft", "Draft"], ["in_progress", "In Progress"], ["completed", "Completed"], ["deleted", "Deleted"]].map(([k, l]) => (
            <button key={k || "all"} data-testid={`fbsign-filter-${k || "all"}`} onClick={() => setFilter(k)}
              className={`px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider transition-colors ${filter === k ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--navy)]"}`}>
              {l}{k && k !== "deleted" && counts[k] ? ` ${counts[k]}` : ""}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <button data-testid="fbsign-start" onClick={() => setWizard(true)} className="btn-primary flex items-center gap-1.5 text-xs !py-1.5 !px-3"><Plus size={12} /> Start now</button>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="tbl tbl-slim">
          <thead><tr><th>Document</th><th>Recipient</th><th className="w-24">Created</th><th className="w-24">Sent</th><th className="w-28">Status</th><th className="w-40 text-right">Actions</th></tr></thead>
          <tbody>
            {shown.map((d) => {
              const [label, cls] = STATUS[d.status] || STATUS.draft;
              const signed = d.recipients.filter((r) => r.status === "signed").length;
              return (
                <tr key={d.id} data-testid={`fbsign-row-${d.id}`}>
                  <td className="font-medium">{d.name}</td>
                  <td>{d.recipients[0] ? d.recipients[0].name : "—"}{d.recipients.length > 1 && <span className="text-[var(--ink-2)]"> +{d.recipients.length - 1}</span>}</td>
                  <td className="font-mono-data text-[10px] whitespace-nowrap">{fmt(d.created_at)}</td>
                  <td className="font-mono-data text-[10px] whitespace-nowrap">{fmt(d.sent_at)}</td>
                  <td>
                    <span className={`inline-flex text-[9px] uppercase tracking-wider px-1.5 py-px rounded-sm border ${cls}`} data-testid={`fbsign-status-${d.id}`}>{label}</span>
                    {d.status === "in_progress" && d.recipients.length > 1 && <span className="ml-1 text-[10px] font-mono-data text-[var(--ink-2)]">{signed}/{d.recipients.length}</span>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <Icon tid={`fbsign-view-${d.id}`} title="View" onClick={() => setViewing(d)}><Eye size={12} /></Icon>
                    {d.status === "in_progress" && <Icon tid={`fbsign-resend-${d.id}`} title="Resend email" onClick={() => resend(d)}><RotateCw size={12} /></Icon>}
                    {d.status === "in_progress" && <Icon tid={`fbsign-copy-${d.id}`} title="Copy link" onClick={() => copyLink(d)}><Link2 size={12} /></Icon>}
                    {d.has_signed_pdf && <Icon tid={`fbsign-download-${d.id}`} title="Download signed PDF" onClick={() => downloadBlob(`/fbsign/${d.id}/signed-pdf`, `${d.name} - signed.pdf`)}><FileDown size={12} /></Icon>}
                    {d.status !== "deleted" && <Icon tid={`fbsign-delete-${d.id}`} title="Delete" danger onClick={() => remove(d)}><Trash2 size={12} /></Icon>}
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && <tr><td colSpan={6} className="text-center !py-8 text-[var(--ink-2)]" data-testid="fbsign-empty">No documents yet. Click <b>Start now</b> to send one for signature.</td></tr>}
          </tbody>
        </table>
      </div>

      {wizard && <NewDocWizard onClose={() => setWizard(false)} onDone={load} />}
      {viewing && <DocView id={viewing.id} onClose={() => setViewing(null)} onChanged={load} />}
    </div>
  );
}

function Icon({ tid, title, onClick, danger, children }) {
  return (
    <button data-testid={tid} title={title} onClick={onClick}
      className={`p-1 rounded-sm transition-colors ${danger ? "text-[var(--ink-2)] hover:text-[var(--err)]" : "text-[var(--ink-2)] hover:text-[var(--navy)] hover:bg-[var(--bg-2)]"}`}>{children}</button>
  );
}

function DocView({ id, onClose }) {
  const [d, setD] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  useEffect(() => {
    api.get(`/fbsign/${id}`).then(async (r) => {
      setD(r.data);
      if (r.data.file_type === "pdf") {
        const f = await api.get(`/fbsign/${id}/file`, { responseType: "blob" });
        setPdfUrl(URL.createObjectURL(new Blob([f.data], { type: "application/pdf" })));
      }
    });
  }, [id]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="fbsign-view">
      <div className="bg-white rounded-sm w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--line)]">
          <div className="font-medium text-sm">{d?.name || "Loading…"}</div>
          <button data-testid="fbsign-view-close" onClick={onClose} className="text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={15} /></button>
        </div>
        {!d ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 flex-1 min-h-0">
            <div className="md:col-span-2 border-r border-[var(--line)] min-h-[300px] overflow-auto bg-[var(--bg-2)]">
              {d.file_type === "pdf"
                ? (pdfUrl ? <iframe title="document" src={pdfUrl} className="w-full h-[70vh]" /> : <div className="p-6 text-xs">Loading document…</div>)
                : <div className="prose max-w-none text-sm bg-white m-4 p-6" dangerouslySetInnerHTML={{ __html: d.html || "" }} />}
            </div>
            <div className="p-4 text-xs space-y-3 overflow-y-auto">
              <div><div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)]">Status</div><div className="text-sm">{(STATUS[d.status] || STATUS.draft)[0]}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)]">Reminders</div><div>{d.reminder.replace(/_/g, " ")}</div></div>
              {d.message && <div><div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)]">Message</div><div className="whitespace-pre-line">{d.message}</div></div>}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] mb-1">Recipients</div>
                {d.recipients.map((r) => (
                  <div key={r.rid} className="py-1 border-b border-[var(--line)] last:border-0" data-testid={`fbsign-view-recipient-${r.rid}`}>
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[var(--ink-2)]">{r.email}</div>
                    <div className={r.status === "signed" ? "text-[var(--ok)]" : "text-[var(--warn)]"}>{r.status === "signed" ? `Signed ${fmt(r.signed_at)}` : "Awaiting signature"}</div>
                  </div>
                ))}
              </div>
              <Timeline events={d.events || []} />
              {d.has_signed_pdf && (
                <button data-testid="fbsign-view-download" onClick={() => downloadBlob(`/fbsign/${d.id}/signed-pdf`, `${d.name} - signed.pdf`)} className="btn-primary w-full flex items-center justify-center gap-1.5 text-xs !py-1.5"><FileDown size={12} /> Signed PDF</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const EVENT_LABEL = {
  created: "Created", sent: "Sent by email", marked_sent: "Marked as sent", link_copied: "Signing link generated",
  emailed: "Email delivered to", opened: "Opened by", reminded: "Reminder sent to", signed: "Signed by",
  completed: "Completed — signed PDF ready", deleted: "Deleted",
};

function Timeline({ events }) {
  return (
    <div data-testid="fbsign-activity">
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] mb-1">Activity</div>
      {events.length === 0 ? <div className="text-[var(--ink-2)]">No activity yet.</div> : (
        <ol className="relative border-l border-[var(--line)] ml-1.5 space-y-2">
          {[...events].reverse().map((e, i) => (
            <li key={i} className="pl-3 relative" data-testid={`fbsign-event-${e.type}`}>
              <span className={`absolute -left-[4.5px] top-1 w-2 h-2 rounded-full ${e.type === "signed" || e.type === "completed" ? "bg-[var(--ok)]" : e.type === "deleted" ? "bg-[var(--err)]" : "bg-[var(--navy)]"}`} />
              <div>{EVENT_LABEL[e.type] || e.type}{e.recipient ? ` ${e.recipient}` : ""}{e.by && !e.recipient ? ` · ${e.by}` : ""}</div>
              <div className="text-[10px] text-[var(--ink-2)] font-mono-data">{new Date(e.at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}{e.ip ? ` · IP ${e.ip}` : ""}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
